import type { Direction } from "../../../engine/direction";
import { colorConstant, hold, lerp, randomAngle, randomBetween, stopAfter } from "../interpolation";
import type { PropertyFn } from "../interpolation";
import { Particle } from "../particle";

const GRAVITY = 50;
const MOTION_STOP = 0.8;

/**
 * Ground position moves linearly toward landing point while a parabolic arc
 * lifts the particle up and back down visually (subtracted from screen Y).
 */
function shrapnelMotion(
	groundVx: number,
	groundVy: number,
	arcSpeed: number,
	gravity: number,
): PropertyFn<{ x: number; y: number }> {
	return (elapsed) => {
		const t = elapsed / 1000;
		const arcHeight = arcSpeed * t - 0.5 * gravity * t * t;
		return {
			x: groundVx * t,
			y: groundVy * t - arcHeight,
		};
	};
}

export class LargeRobotDebrisParticle extends Particle {
	constructor(originX: number, originY: number, _direction?: Direction) {
		const arcSpeed = randomBetween(12, 20);
		const landingTime = (2 * arcSpeed) / GRAVITY;

		// Scatter landing position in a circle around origin
		const scatterAngle = randomAngle();
		const scatterDistance = randomBetween(3, 6);
		const groundVx = (Math.cos(scatterAngle) * scatterDistance) / landingTime;
		const groundVy = (Math.sin(scatterAngle) * scatterDistance) / landingTime;

		const sizeStart = randomBetween(0.8, 0.9);
		const startAngle = randomAngle();
		const ttl = (landingTime * 1000) / MOTION_STOP;

		super(originX, originY, {
			ttl,
			position: stopAfter(shrapnelMotion(groundVx, groundVy, arcSpeed, GRAVITY), MOTION_STOP),
			size: lerp(sizeStart, sizeStart),
			opacity: hold(1.0, MOTION_STOP, lerp(1.0, 0.0)),
			color: colorConstant(70, 70, 75),
			rotation: stopAfter(lerp(startAngle, startAngle + randomBetween(2, 12)), MOTION_STOP),
		});
	}
}
